import apiClient from './apiClient';
import type {
  Group,
  GroupMembership,
  GroupStatistics,
  CreateGroupDTO,
  UpdateGroupDTO,
  GroupMember,
  GroupTestResult,
  JoinRequest,
} from '../types/group';

/**
 * Group Service - API calls for group management
 */
const groupService = {
  // ==================== Manager Functions ====================

  /**
   * Create a new group (Manager only)
   */
  async createGroup(data: CreateGroupDTO): Promise<Group> {
    const response = await apiClient.post<{ success: boolean; data: Group }>('/groups', data);
    return response.data.data;
  },

  /**
   * Get all groups managed by the current user
   */
  async getMyGroups(): Promise<Group[]> {
    const response = await apiClient.get<{ success: boolean; data: Group[] }>('/groups/my-groups');
    return response.data.data;
  },

  /**
   * Get group details by ID
   */
  async getGroupById(id: string): Promise<Group> {
    const response = await apiClient.get<{ success: boolean; data: Group }>(`/groups/${id}`);
    return response.data.data;
  },

  /**
   * Update group information (Owner only)
   */
  async updateGroup(id: string, data: UpdateGroupDTO): Promise<Group> {
    const response = await apiClient.put<{ success: boolean; data: Group }>(`/groups/${id}`, data);
    return response.data.data;
  },

  /**
   * Soft delete a group (Owner only)
   */
  async deleteGroup(id: string): Promise<void> {
    await apiClient.delete(`/groups/${id}`);
  },

  /**
   * Regenerate invite code for a group (Owner only)
   */
  async regenerateInviteCode(id: string): Promise<Group> {
    const response = await apiClient.post<{ success: boolean; data: Group }>(
      `/groups/${id}/regenerate-code`
    );
    return response.data.data;
  },

  /**
   * Get all members of a group
   */
  async getGroupMembers(id: string): Promise<GroupMember[]> {
    const response = await apiClient.get<{ success: boolean; data: { members: GroupMember[] } }>(
      `/groups/${id}/members`
    );
    return response.data.data.members;
  },

  /**
   * Add a member to the group directly (Owner only)
   */
  async addMember(groupId: string, userId: string): Promise<GroupMembership> {
    const response = await apiClient.post<{ success: boolean; data: GroupMembership }>(
      `/groups/${groupId}/members`,
      { user_id: userId }
    );
    return response.data.data;
  },

  /**
   * Remove a member from the group (Owner only)
   */
  async removeMember(groupId: string, userId: string): Promise<void> {
    await apiClient.delete(`/groups/${groupId}/members/${userId}`);
  },

  /**
   * Get group statistics (Owner only)
   */
  async getGroupStatistics(groupId: string): Promise<GroupStatistics> {
    const response = await apiClient.get<{ success: boolean; data: GroupStatistics }>(
      `/groups/${groupId}/statistics`
    );
    return response.data.data;
  },

  /**
   * Get group test analysis (Manager only)
   */
  async getGroupTestAnalysis(groupId: string): Promise<GroupStatistics> {
    const response = await apiClient.get<{ success: boolean; data: GroupStatistics }>(
      `/groups/${groupId}/test-analysis`
    );
    return response.data.data;
  },

  /**
   * Get all test results for the group (Owner only)
   */
  async getGroupTestResults(groupId: string): Promise<GroupTestResult[]> {
    const response = await apiClient.get<{ success: boolean; data: { tests: GroupTestResult[] } }>(
      `/groups/${groupId}/test-results`
    );
    return response.data.data.tests;
  },

  /**
   * Get test results for a specific member (Owner only)
   */
  async getMemberTests(groupId: string, userId: string): Promise<GroupTestResult[]> {
    const response = await apiClient.get<{ success: boolean; data: { tests: GroupTestResult[] } }>(
      `/groups/${groupId}/members/${userId}/tests`
    );
    return response.data.data.tests;
  },

  // ==================== Student Functions ====================

  /**
   * Preview a group before joining (using invite code)
   */
  async previewGroup(inviteCode: string): Promise<Group> {
    const response = await apiClient.get<{ success: boolean; data: { group: Group } }>(
      `/groups/join/${inviteCode}`
    );
    return response.data.data.group;
  },

  /**
   * Request to join a group using invite code (creates pending membership)
   */
  async joinGroup(inviteCode: string): Promise<GroupMembership> {
    const response = await apiClient.post<{ success: boolean; data: GroupMembership }>(
      `/groups/join/${inviteCode}`
    );
    return response.data.data;
  },

  /**
   * Get all groups the current user is a member of (or has pending requests for)
   */
  async getMyMemberships(): Promise<GroupMembership[]> {
    const response = await apiClient.get<{ success: boolean; data: GroupMembership[] }>(
      '/groups/my-memberships'
    );
    return response.data.data;
  },

  /**
   * Leave a group
   */
  async leaveGroup(groupId: string): Promise<void> {
    await apiClient.post(`/groups/${groupId}/leave`);
  },

  // ==================== Manager Approval Functions ====================

  /**
   * Get pending join requests for a group (Manager only)
   */
  async getPendingJoinRequests(groupId: string): Promise<JoinRequest[]> {
    const response = await apiClient.get<{ success: boolean; data: JoinRequest[] }>(
      `/groups/${groupId}/join-requests`
    );
    return response.data.data;
  },

  /**
   * Approve a join request (Manager only)
   */
  async approveJoinRequest(groupId: string, membershipId: string): Promise<GroupMembership> {
    const response = await apiClient.post<{ success: boolean; data: GroupMembership }>(
      `/groups/${groupId}/join-requests/${membershipId}/approve`
    );
    return response.data.data;
  },

  /**
   * Reject a join request (Manager only)
   */
  async rejectJoinRequest(groupId: string, membershipId: string): Promise<void> {
    await apiClient.post(`/groups/${groupId}/join-requests/${membershipId}/reject`);
  },
};

export { groupService };
export default groupService;
